import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import { DataTable, NextButton, Notice, OptionAutocomplete, OptionSelect } from '../../../../ui';
import { colors } from '../../../../theme/tokens';
import actions from '../../../../redux/actions';
import { OperationStatus } from '../../../../types/OperationStatus';
import { DesktopType } from '../../../../types/DesktopType';
import { UserStatus } from '../../../../types/UserStatus';
import { DevicePlatform } from '../../../../types/DevicePlatform';

const mapStateToProps = (state) => {
  return {
    addBrowserStatus: state.addBrowserStatus,
    userSaveStatus: state.userSaveStatus,
    user: state.user,
    isMobile: state.resolution.device === 'mobile' || state.resolution.device === 'mobile-tablet',
    browserNames: state.browsers.names,
    browserVersions: state.browsers.versions,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    updateUser: (data) => dispatch(actions.updateUser(data)),
    saveUser: (data) => dispatch(actions.saveUser(data)),
    addBrowser: (data) => dispatch(actions.addBrowser(data)),
    deleteBrowser: (data) => dispatch(actions.deleteBrowser(data)),
  };
};

class Browsers extends Component<any> {
  public state: any;
  private desktopTypeOptions: any[];
  private browserOptions: any[];

  constructor(props) {
    super(props);
    this.state = {
      newBrowserId: '',
      newBrowserVersionId: '',
      browserVersions: [],
    };

    this.desktopTypeOptions = Object.keys(DesktopType).map((el) => ({
      key: el,
      value: DesktopType[el],
      text: el,
    }));

    this.browserOptions = this.props.browserNames.map((el) => ({
      key: el.id,
      value: el.id,
      text: el.name,
    }));
  }

  componentDidMount() {
    if (!this.props.user.desktopTypes.length) {
      this.props.updateUser({
        ...this.props.user,
        desktopTypes: [DesktopType.Windows],
      });
    }
  }

  componentDidUpdate(prevProps) {
    if (
      this.props.userSaveStatus.status !== prevProps.userSaveStatus.status &&
      this.props.userSaveStatus.status === OperationStatus.success
    ) {
      this.props.updateUser({ status: UserStatus.EvalCyclePending });
      this.props.history.push('/account/tester');
    }
  }

  onChange = (event, data) => {
    const user = { ...this.props.user };

    switch (data.name) {
      case 'desktopType': {
        user.desktopTypes = [data.value];
        this.props.updateUser(user);
        break;
      }

      case 'newBrowserId': {
        const browserVersions = this.props.browserVersions.filter(
          (el) => el.browserId === data.value
        );

        return this.setState({
          ...this.state,
          newBrowserId: data.value,
          newBrowserVersionId: '',
          browserVersions,
        });
      }

      case 'newBrowserVersionId': {
        return this.setState({
          ...this.state,
          newBrowserVersionId: data.value,
        });
      }

      default:
        return;
    }
  };

  onAddBrowser = () => {
    // TODO maybe remove this and call from render()?
    this.props.addBrowser({
      platformId: DevicePlatform.Desktop,
      browserId: this.state.newBrowserId,
      browserVersionId: this.state.newBrowserVersionId,
    });
  };

  isBrowserAlreadyExists = (browser) => {
    const result = this.props.user.browsers.find(
      (el) => el.browserId === browser.id && el.browserVersionId === browser.versionId
    );

    if (result) {
      return true;
    }

    return false;
  };

  onDeleteBrowser = (event, data) => {
    // Same shape as the stored entry: the server removes it by deep equality
    this.props.deleteBrowser({
      platformId: data.platformId,
      browserId: data.browserId,
      browserVersionId: data.browserVersionId,
    });
  };

  onSave = () => {
    this.props.updateUser({ status: UserStatus.EvalCyclePending });
    this.props.saveUser(this.props.user);
  };

  render() {
    const browserVersionOptions = this.props.browserVersions
      .filter((el) => el.browserId === this.state.newBrowserId)
      .map((el) => ({
        key: el.id,
        value: el.id,
        text: el.version,
      }))
      .reverse();

    const processing = this.props.addBrowserStatus.status === OperationStatus.processing;
    const alreadyAdded = this.isBrowserAlreadyExists({
      id: this.state.newBrowserId,
      versionId: this.state.newBrowserVersionId,
    });
    const disabled =
      processing ||
      !this.state.newBrowserId ||
      !this.state.newBrowserVersionId ||
      alreadyAdded;

    const browserNames = this.props.browserNames;
    const browserVersions = this.props.browserVersions;

    const user = this.props.user;
    const saveDisabled =
      (user.desktopTypes.includes(DesktopType.Windows) ||
        user.desktopTypes.includes(DesktopType.Mac)) &&
      !user.browsers.length;

    const change = (name: string) => (value: any) => this.onChange(null, { name, value: value ?? '' });

    // Unknown catalogue ids used to crash this page; show them instead
    const rows: any[] = user.browsers.map((el) => {
      const browser = browserNames.find((b) => b.id === el.browserId);
      const version = browserVersions.find((v) => v.id === el.browserVersionId);
      return {
        ...el,
        browser: browser ? browser.name : `Unknown browser ${el.browserId}`,
        version: version ? version.version : `Unknown browser version ${el.browserVersionId}`,
      };
    });

    const title = (label: string) => (
      <Typography component="h3" sx={{ fontSize: 15, fontWeight: 600, color: colors.titleBlue, mb: 1.5 }}>{label}</Typography>
    );

    return (
      <>
        <Notice title="Register your desktop type and browsers" />

        <Box sx={{ maxWidth: 360 }}>
          {title('Your desktop computer type')}
          <OptionSelect label="Desktop type" options={this.desktopTypeOptions} value={[...user.desktopTypes][0]} onValueChange={change('desktopType')} />
        </Box>

        {!user.desktopTypes.includes(DesktopType.None) && (
          <>
            <Box>
              {title('Add desktop browser')}
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr auto' }, gap: 1.5, alignItems: 'start', maxWidth: 760 }}>
                <OptionSelect label="Browser" options={this.browserOptions} value={this.state.newBrowserId} onValueChange={change('newBrowserId')} />
                <OptionAutocomplete label="Version" options={browserVersionOptions} value={this.state.newBrowserVersionId}
                  onValueChange={change('newBrowserVersionId')} disabled={!this.state.newBrowserId} />
                <Button variant="contained" startIcon={<AddIcon />} disabled={disabled} onClick={this.onAddBrowser} sx={{ height: 56 }}>
                  {processing ? 'Adding…' : 'Add'}
                </Button>
              </Box>
              {alreadyAdded && (
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>This browser is already in your list.</Typography>
              )}
            </Box>

            <Box>
              {title('My browsers')}
              <DataTable
                rows={rows}
                rowKey={(row) => `${row.platformId}-${row.browserId}-${row.browserVersionId}`}
                emptyText="You haven't added any browsers yet"
                columns={[
                  { key: 'browser', label: 'Browser', render: (row) => row.browser },
                  { key: 'version', label: 'Version', render: (row) => row.version },
                  {
                    key: 'actions',
                    align: 'right',
                    render: (row) => (
                      <Button size="small" color="error" onClick={() => this.onDeleteBrowser(null, { platformId: row.platformId, browserId: row.browserId, browserVersionId: row.browserVersionId })}>
                        Delete
                      </Button>
                    ),
                  },
                ]}
              />
            </Box>
          </>
        )}

        <NextButton onClick={this.onSave} disabled={saveDisabled} />
      </>
    );
  }
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Browsers));
